import type { QuestionMetadata } from '../types/api';

export class QuestionCodeMappingError extends Error {
  code: string;
  missingFields: string[];

  constructor(missingFields: string[]) {
    super(`Missing Q-code mapping for: ${missingFields.join(', ')}`);
    this.name = 'QuestionCodeMappingError';
    this.code = 'MISSING_Q_MAPPING';
    this.missingFields = missingFields;
  }
}

export class QuestionValueNormalizationError extends Error {
  code: string;
  questionName: string;
  rawValue: unknown;

  constructor(questionName: string, rawValue: unknown) {
    super(`Missing English normalization for question '${questionName}' value '${rawValue}'`);
    this.name = 'QuestionValueNormalizationError';
    this.code = 'MISSING_Q_VALUE_MAPPING';
    this.questionName = questionName;
    this.rawValue = rawValue;
  }
}

interface SurveyChoice {
  value?: string;
  text?: string | Record<string, string>;
}

interface SurveyMatrixEntry {
  value?: string;
  text?: string | Record<string, string>;
}

interface SurveyElement {
  type?: string;
  name?: string;
  elements?: SurveyElement[];
  choices?: (string | SurveyChoice)[];
  hasOther?: boolean;
  otherText?: string | Record<string, string>;
  rows?: (string | SurveyMatrixEntry)[];
  columns?: (string | SurveyMatrixEntry)[];
}

interface SurveyPage {
  name?: string;
  elements?: SurveyElement[];
}

interface SurveyDefinition {
  pages?: SurveyPage[];
}

const STRING_CHOICE_ENGLISH_OVERRIDES: Record<string, Record<string, string>> = {
  drupal_options: {
    'Upsum (Platform.sh) (PaaS especializado en Drupal)': 'Upsum (Platform.sh) (Drupal-specialized PaaS)',
    'Amazee.io (PaaS especializado en Drupal)': 'Amazee.io (Drupal-specialized PaaS)',
    'Acquia (PaaS especializado en Drupal)': 'Acquia (Drupal-specialized PaaS)',
    'Pantheon (PaaS especializado en Drupal)': 'Pantheon (Drupal-specialized PaaS)',
  },
  other_options: {
    'Cloud / Infraestructura (AWS, GCP, Azure, DigitalOcean, Otro)':
      'Cloud / Infrastructure (AWS, GCP, Azure, DigitalOcean, Other)',
    'Kubernetes gestionado (EKS, GKE, AKS)': 'Managed Kubernetes (EKS, GKE, AKS)',
    'VPS / Hosting tradicional': 'VPS / Traditional hosting',
    'Hosting administrado no especializado': 'Managed hosting (non-specialized)',
  },
};

function getLocalizedText(
  textValue: string | Record<string, string> | undefined,
  locale: string
): string {
  if (!textValue) {
    return '';
  }

  if (typeof textValue === 'string') {
    return textValue;
  }

  if (typeof textValue === 'object') {
    return textValue[locale] || textValue.en || textValue.es || '';
  }

  return '';
}

function normalizeStringChoiceEnglish(
  questionName: string,
  choiceValue: string
): string {
  return STRING_CHOICE_ENGLISH_OVERRIDES[questionName]?.[choiceValue] || choiceValue;
}

function buildChoiceLookup(
  choices: (string | SurveyChoice)[],
  questionName: string,
  hasOther: boolean,
  otherText: string | Record<string, string> | undefined
): Record<string, string> {
  const lookup: Record<string, string> = {};

  choices.forEach((choice) => {
    if (typeof choice === 'string') {
      const english = normalizeStringChoiceEnglish(questionName, choice);
      lookup[String(choice)] = english;
      return;
    }

    if (!choice || typeof choice !== 'object') {
      return;
    }

    const rawValue = choice.value ?? getLocalizedText(choice.text, 'en');
    const englishText = getLocalizedText(choice.text, 'en') || String(rawValue);
    const spanishText = getLocalizedText(choice.text, 'es');

    lookup[String(rawValue)] = englishText;

    if (spanishText) {
      lookup[String(spanishText)] = englishText;
    }
  });

  if (hasOther) {
    const englishOther = getLocalizedText(otherText, 'en') || 'Other';
    const spanishOther = getLocalizedText(otherText, 'es');

    lookup.other = englishOther;
    lookup[englishOther] = englishOther;

    if (spanishOther) {
      lookup[spanishOther] = englishOther;
    }
  }

  return lookup;
}

function buildMatrixLookup(entries: (string | SurveyMatrixEntry)[]): Record<string, string> {
  const lookup: Record<string, string> = {};

  entries.forEach((entry) => {
    if (typeof entry === 'string') {
      lookup[entry] = entry;
      return;
    }

    if (!entry || typeof entry !== 'object') {
      return;
    }

    const rawValue = entry.value ?? getLocalizedText(entry.text, 'en');
    const englishText = getLocalizedText(entry.text, 'en') || String(rawValue);
    const spanishText = getLocalizedText(entry.text, 'es');

    lookup[String(rawValue)] = englishText;

    if (spanishText) {
      lookup[String(spanishText)] = englishText;
    }
  });

  return lookup;
}

function collectQuestionNames(elements: SurveyElement[], out: string[]): void {
  elements.forEach((element) => {
    if (element?.type !== 'panel' && element?.name) {
      out.push(element.name);
    }

    if (Array.isArray(element?.elements)) {
      collectQuestionNames(element.elements, out);
    }
  });
}

function collectQuestions(elements: SurveyElement[], out: SurveyElement[]): void {
  elements.forEach((element) => {
    if (element?.type !== 'panel' && element?.name) {
      out.push(element);
    }

    if (Array.isArray(element?.elements)) {
      collectQuestions(element.elements, out);
    }
  });
}

export function buildQuestionCodeMap(surveyDefinition: SurveyDefinition): Record<string, string> {
  const names: string[] = [];

  (surveyDefinition?.pages || []).forEach((page) => {
    collectQuestionNames(page.elements || [], names);
  });

  return names.reduce((acc, name, index) => {
    acc[name] = `Q${index + 1}`;
    return acc;
  }, {} as Record<string, string>);
}

export function buildQuestionMetadataMap(
  surveyDefinition: SurveyDefinition
): Record<string, QuestionMetadata> {
  const questions: SurveyElement[] = [];

  (surveyDefinition?.pages || []).forEach((page) => {
    collectQuestions(page.elements || [], questions);
  });

  return questions.reduce((acc, question) => {
    const metadata: QuestionMetadata = {
      type: question.type || 'unknown',
    };

    if (
      ['radiogroup', 'dropdown', 'checkbox', 'ranking', 'tagbox'].includes(question.type || '')
    ) {
      metadata.choiceLookup = buildChoiceLookup(
        question.choices || [],
        question.name || '',
        Boolean(question.hasOther),
        question.otherText
      );
    }

    if (question.type === 'matrix') {
      metadata.rowLookup = buildMatrixLookup(question.rows || []);
      metadata.columnLookup = buildMatrixLookup(question.columns || []);
    }

    if (question.name) {
      acc[question.name] = metadata;
    }
    return acc;
  }, {} as Record<string, QuestionMetadata>);
}

function normalizeChoiceValue(
  questionName: string,
  rawValue: unknown,
  lookup: Record<string, string>
): unknown {
  if (rawValue === null || typeof rawValue === 'undefined') {
    return rawValue;
  }

  const normalized = lookup[String(rawValue)];

  if (typeof normalized === 'undefined') {
    throw new QuestionValueNormalizationError(questionName, rawValue);
  }

  return normalized;
}

function combineOtherWithComment(
  normalizedValue: string,
  questionName: string,
  originalAnswers: Record<string, unknown>
): string {
  const otherKey = `${questionName}-Comment`;
  const commentValue = originalAnswers[otherKey];

  if (typeof commentValue === 'string' && commentValue.trim()) {
    return `${normalizedValue}: ${commentValue.trim()}`;
  }

  return normalizedValue;
}

function normalizeByQuestionType(
  questionName: string,
  rawValue: unknown,
  metadata: QuestionMetadata | undefined,
  originalAnswers: Record<string, unknown> = {}
): unknown {
  if (!metadata) {
    return rawValue;
  }

  if (['radiogroup', 'dropdown'].includes(metadata.type)) {
    const normalized = normalizeChoiceValue(
      questionName,
      rawValue,
      metadata.choiceLookup || {}
    );
    if (typeof normalized === 'string' && normalized === 'Other') {
      return combineOtherWithComment(normalized, questionName, originalAnswers);
    }
    return normalized;
  }

  if (['checkbox', 'ranking', 'tagbox'].includes(metadata.type)) {
    if (!Array.isArray(rawValue)) {
      throw new QuestionValueNormalizationError(questionName, rawValue);
    }

    return rawValue.map((item) => {
      const normalized = normalizeChoiceValue(questionName, item, metadata.choiceLookup || {});
      if (typeof normalized === 'string' && normalized === 'Other') {
        return combineOtherWithComment(normalized, questionName, originalAnswers);
      }
      return normalized;
    });
  }

  if (metadata.type === 'matrix') {
    if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
      throw new QuestionValueNormalizationError(questionName, rawValue);
    }

    const normalized: Record<string, unknown> = {};

    Object.entries(rawValue).forEach(([rowKey, colValue]) => {
      const rowEnglish = normalizeChoiceValue(
        questionName,
        rowKey,
        metadata.rowLookup || {}
      );
      const colEnglish = normalizeChoiceValue(
        questionName,
        colValue,
        metadata.columnLookup || {}
      );
      normalized[String(rowEnglish)] = colEnglish;
    });

    return normalized;
  }

  return rawValue;
}

export function toQuestionCodeAnswers(
  answersByFieldName: Record<string, unknown>,
  questionCodeMap: Record<string, string>,
  questionMetadataMap: Record<string, QuestionMetadata> = {}
): Record<string, unknown> {
  const mapped: Record<string, unknown> = {};
  const missingFields: string[] = [];

  Object.entries(answersByFieldName).forEach(([fieldName, value]) => {
    if (fieldName.endsWith('-Comment')) {
      return;
    }

    const code = questionCodeMap[fieldName];

    if (!code) {
      missingFields.push(fieldName);
      return;
    }

    const normalizedValue = normalizeByQuestionType(
      fieldName,
      value,
      questionMetadataMap[fieldName],
      answersByFieldName
    );
    mapped[code] = normalizedValue;
  });

  if (missingFields.length > 0) {
    throw new QuestionCodeMappingError(missingFields);
  }

  return mapped;
}
